import { Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { MutableRefObject, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface TruncatedTextProps {
  text: string;
  maxLength?: number;
  showTooltip?: boolean;
  href?: string;
  disabled?: boolean
  clampRef?: MutableRefObject<HTMLElement|null>;
  isEditorial?:boolean;
  actionOnEditorial?:() => string,
  isLinkRelative?:boolean
  webkitLineClamp?: number
  children?: JSX.Element
}

export function TruncatedText ({ 
  text,
  maxLength = 50, 
  showTooltip = true, 
  href, 
  disabled, 
  clampRef,
  isEditorial,
  actionOnEditorial,
  isLinkRelative,
  webkitLineClamp = 2,
  children 
}: TruncatedTextProps) {

  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();

  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!clampRef?.current) return;

    const el = clampRef.current;
    const checkTruncation = () => {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [clampRef, text]);

  return (
    <Tooltip
      aria-hidden="true"
      title={ showTooltip &&  ((text.length > maxLength) || isTruncated) ? (
        text
      ) : null }
      placement={ "top" }
      open={ showMobileTooltip }
      onOpen={ () => setShowMobileTooltip(true) }
      onClose={ () => setShowMobileTooltip(false) }
      onTouchStart={ () => {
        setShowMobileTooltip(true);
      } }
      onClick={ () => {

        if(isEditorial && actionOnEditorial){
          const url = actionOnEditorial();
          if (!url) return;
          if(isLinkRelative && url){
            navigate(url);
          }else{
            window.open(url, "_blank", "noopener,noreferrer");
          }
        }else{
          if(href && !disabled) navigate(href);
        }
      } }
      PopperProps={ { style: { pointerEvents: "none", zIndex: 10001 } } }
      slotProps={ {
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -5]
              }
            }
          ]
        },
        tooltip: {
          style: {
            backgroundColor: alpha(theme.customColors.backgroundPrimary, 0.9),
            color: theme.customColors.textPrimary
          }
        }
      } }
    >
      <span
        style={ {
          cursor: (href && !disabled) ? "pointer" : "inherit" ,
          display: "-webkit-box",
          fontSize: "1rem",
          lineHeight: "1.25rem",
          overflow: "hidden",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: webkitLineClamp
        } }
      >
        { children ?? text }
      </span>
    </Tooltip>
  );
}
